import React, { useState, useEffect } from "react";
import axios, { AxiosRequestHeaders } from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../images/giphy.gif";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";

interface Skill {
  skill_id: number;
  skill_name: string;
  skill_category: string;
  skill_tag: string;
  skill_shortname: string;
}

const Skills = () => {
  const [skillList, setSkillList] = useState<Skill[]>([]);
  const [loader, setLoader] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingSkillId, setPendingSkillId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filteredSkills, setFilteredSkills] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionType, setActionType] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const headers: AxiosRequestHeaders = {
    "x-access-token": localStorage.getItem("jwttoken") || "",
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSkills, setTotalSkills] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const callApiExamsList = async (page = 1) => {
    try {
      setLoader(true);
      setError(null);
      const url = `${process.env.REACT_APP_API_URL}skills/showallskills?page=${page}&limit=100`;
      const response = await axios.get(url);
       const data = response.data;
    setSkillList(data);
    setFilteredSkills(data);
    setTotalSkills(data.length);  
    setCurrentPage(1);
    setTotalPages(1);             
      console.log("This is API Response:", response.data);
    } catch (error: any) {
      setError(error.message || "Failed to fetch skills.");
      setFailureMessage("Failed to fetch skills. Please try again.");
      setShowFailureModal(true);
    } finally {
      setLoader(false);
    }
  };

  const deleteSkill = async (id: number) => {
    try {
      setLoader(true);
      const url = `${process.env.REACT_APP_API_URL}skills/deleteskill/${id}`;
      await axios.put(url,{headers});
      setSuccessMessage("Skill deleted successfully!");
      setShowSuccessModal(true);
      callApiExamsList(currentPage);
    } catch (error: any) {
      console.error("Error deleting skill:", error);
      setFailureMessage("Failed to delete skill. Please try again.");
      setShowFailureModal(true);
    } finally {
      setLoader(false);
    }
  };

  const confirmDeleteSkill = (id: number) => {
    setPendingSkillId(id);
    setModalMessage("Are you sure you want to delete this skill?");
    setActionType("Delete");
    setModalShow(true);
  };

  const handleDeleteConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && pendingSkillId !== null) {
      await deleteSkill(pendingSkillId);
    }
    setPendingSkillId(null);
  };

  useEffect(() => {
    callApiExamsList(currentPage);
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const filtered = searchTerm
        ? skillList.filter(
            (skill) =>
              skill.skill_name.toLowerCase().includes(searchTerm) ||
              skill.skill_tag.toLowerCase().includes(searchTerm) ||
              skill.skill_category.toLowerCase().includes(searchTerm)
          )
        : skillList;

      setFilteredSkills(filtered);
      setCurrentPage(1);
      setTotalPages(Math.ceil(filtered.length / pageSize));
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, skillList]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, filteredSkills.length);
  const currentSkills = filteredSkills.slice(startEntry - 1, endEntry);

  return (
    <div className="container-fluid p-0">
      <div className="row g-0">
        <div className="col-12">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-black fw-bold">Skills Management</h2>
        <div className="d-flex gap-2 align-items-center w-50">
          <span className="fw-bold text-secondary text-nowrap">
            {totalSkills} results
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search by Skill Name, Tag or Category"
            value={searchTerm}
            onChange={handleSearch}
          />
          <Link to="/skillsadd" className="btn bg-info text-white">
            + Add Skill
          </Link>
        </div>
      </div>

      <div className="card shadow-sm border-0 rounded" style={{ maxHeight: 'calc(100vh - 250px)' }}>
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">Skills List</h5>
        </div>
        <div className="card-body p-0 position-relative">
          <div
            className="table-responsive"
            style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}
          >
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light position-sticky top-0" >
                <tr>
                  <th>SR. No.</th>
                  <th>Skill Name</th>
                  <th>Skill Category</th>
                  <th>Skill Tag</th>
                  <th>Skill ShortName</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loader ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4">
                      <img src={loaderimage} alt="Loading..." height="40" />
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center text-danger fw-bold py-3"
                    >
                      <div
                        className="alert alert-danger text-center m-3"
                        role="alert"
                      >
                        Oops! Something went wrong. Please try again later.
                      </div>
                    </td>
                  </tr>
                ) : currentSkills.length > 0 ? (
                  currentSkills.map((skill, index) => (
                    <tr key={index} className="border-bottom">
                      <td>{startEntry + index}</td>
                      <td>{skill.skill_name}</td>
                      <td>{skill.skill_category}</td>
                      <td>{skill.skill_tag}</td>
                      <td>{skill.skill_shortname}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <Link
                            to={`/skillsupdate/${skill.skill_id}`}
                            className="btn btn-outline-warning"
                          >
                            Edit
                          </Link>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => confirmDeleteSkill(skill.skill_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted fw-bold">
                      <div
                        className="alert alert-secondary text-center m-3"
                        role="alert"
                      >
                        No Skills Available
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="position-sticky bottom-0 bg-white py-2 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted ps-3">
                    Showing {startEntry}-{endEntry} of {filteredSkills.length} entries
                  </div>
                  <nav>
                    <ul className="pagination mb-0 pe-3">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li 
                          key={index} 
                          className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
        </div>
      </div>
      <ConfirmationModal
        show={modalShow}
        modalMessage={modalMessage}
        onHide={() => setModalShow(false)}
        confirmation={handleDeleteConfirmation}
        operationType={actionType}
      />
      <NewconfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        modalMessage={successMessage}
        operationType="OK"
        timeoutDuration={3000}
      />
      <AllFailureModal
        show={showFailureModal}
        onHide={() => setShowFailureModal(false)}
        modalMessage={failureMessage}
        operationType="OK"
        timeoutDuration={3000}
      />
    </div>
    </div>
  </div>
  );
};

export default Skills;
